import { DialogProps } from "@mui/material";
import { produce } from "immer";
import React from "react";
import { create } from "zustand";

export interface PushModalPayload extends Omit<DialogProps, "open" | "title" | "onClose" | "content"> {
  title?: React.ReactNode;
  content?: React.ReactNode;
  slide?: boolean;
  onClose?: () => void;
  hideClose?: boolean;
  disablePadding?: boolean;
}

export interface ModalStackItem extends PushModalPayload {
  open: boolean;
}

/**
 * `popModal` and `destroyModal` both exist in order to preserve the modal's transition
 * animation while closing. You shouldn't ever need to call `destroyModal` manually as
 * this is handled in the root modal component.
 */
export interface ModalStore {
  stack: ModalStackItem[];
  actions: {
    pushModal: (payload: PushModalPayload) => void;
    popModal: () => void;
    destroyModal: () => void;
    resetModal: () => void;
  };
}

const initialState: Omit<ModalStore, "actions"> = {
  stack: [],
};

export const useModalStore = create<ModalStore>((set) => ({
  ...initialState,

  actions: {
    pushModal: (payload) => {
      set(
        produce((state) => {
          state.stack.push({ open: true, ...payload });
        })
      );
    },
    popModal: () => {
      set(
        produce((state) => {
          if (state.stack.length) state.stack[state.stack.length - 1].open = false;
        })
      );
    },
    destroyModal: () => {
      set(
        produce((state) => {
          state.stack.pop();
        })
      );
    },
    resetModal: () => {
      set(initialState);
    },
  },
}));

export const useModalStoreActions = () => useModalStore((state) => state.actions);
